import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { API_BASE_URL } from "../../../src/App.js"
import { useDispatch } from "react-redux";
import { addloginData } from "../../../src/redux/action";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [contact, setContact] = useState(""); // Can be mobile or email
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!contact) {
      setError("Mobile number or email is required");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // Determine if contact is email or mobile
      const isEmail = contact.includes("@");
      const payload = isEmail 
        ? { email: contact, mobile: "", username: "" }
        : { mobile: contact, email: "", username: "" };
      
      const res = await axios.post(`${API_BASE_URL}sendOtp`, payload);
      console.log("OTP sent:", res.data);
      dispatch(addloginData(res?.data));
      setSuccess(true);
      
      // Navigate after a brief delay to show success state
      setTimeout(() => {
        navigate("/verifyotp", { state: { contact } });
      }, 1500);
    } catch (err:any) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-100 via-blue-200 to-sky-300 p-4">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-500/15 to-sky-300/15 rounded-full blur-3xl -z-10 hidden md:block"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-blue-500/15 to-sky-300/15 rounded-full blur-3xl -z-10 hidden md:block"></div>

      <Card className="w-full max-w-md shadow-2xl border border-slate-200/60 hover:shadow-3xl transition-all duration-300">
        {/* Header with branding */}
        <div className="relative bg-gradient-to-br from-blue-950 via-blue-800 to-sky-600 p-8 rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 right-2 w-20 h-20 bg-blue-300/20 rounded-full blur-xl"></div>
            <div className="absolute bottom-2 left-2 w-16 h-16 bg-blue-300/20 rounded-full blur-xl"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-300 via-blue-500 to-sky-600 flex items-center justify-center shadow-lg shadow-blue-500/50 hover:shadow-blue-600/70 transition-all hover:scale-110">
                <Mail className="text-white w-8 h-8" strokeWidth={1.5} />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">Forgot Password?</CardTitle>
            <CardDescription className="text-emerald-200 text-base">
              Don't worry, we'll help you recover your account
            </CardDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5 pt-8 pb-6">
            {/* Error Alert */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200/60 rounded-xl flex gap-3 animate-shake">
                <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200/60 rounded-xl flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-700 text-sm font-medium">OTP sent successfully! Redirecting...</p>
              </div>
            )}

            {/* Info message */}
            <div className="p-4 bg-emerald-50 border border-emerald-200/60 rounded-xl">
              <p className="text-emerald-700 text-sm">
                Enter your mobile number or email and we'll send you a 4-digit OTP to reset your password.
              </p>
            </div>

            {/* Contact Field - Mobile or Email */}
            <div className="space-y-2.5">
              <Label htmlFor="contact" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Mail size={16} className="text-emerald-200" />
                Mobile Number or Email
              </Label>
              <div className="relative group">
                <Input
                  id="contact"
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Enter mobile number or email"
                  required
                  disabled={loading || success}
                  className="pl-4 pr-4 py-3 border-2 border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm transition-all focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10 focus:ring-0 placeholder:text-slate-400 disabled:opacity-50 disabled:bg-slate-50"
                />
                {contact && !loading && !success && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pb-8">
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading || success}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending OTP...
                </>
              ) : success ? (
                <>
                  <CheckCircle size={18} />
                  OTP Sent!
                </>
              ) : (
                <>
                  <Mail size={18} className="group-hover:translate-x-1 transition-transform" />
                  Send OTP
                </>
              )}
            </Button>

            <button
              type="button"
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline decoration-emerald-200 transition-all p-2 hover:bg-emerald-50 rounded-lg w-full flex items-center justify-center gap-2"
              onClick={() => navigate("/")}
              disabled={loading || success}
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
          </CardFooter>
        </form>

        {/* Footer info */}
        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50">
          <p className="text-xs text-slate-500 text-center">
            We'll send a verification code to help you reset your password securely
          </p>
        </div>
      </Card>
    </div>
  );
}
